// ============================================================
// TotalEnrolmentOverTime — replaces app-total-enrollment-over-time
// Loads CSV 10186.csv, aggregates by school_type and academic year, renders bar chart
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Papa from 'papaparse';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  parseCsvToObjects,
  sumField,
  getMngTypeOnUserType,
} from '../../shared/utils';
import { RootState } from '../../../../store';
import ChartPanelWrapper from '../../../../shared/graph/graphPanelWrapper';
import SharedFilter from '../../../../shared/filter/sharedFilter';
import { FilterState } from '../../../../shared/filter/filterTypes';
import '../../../../shared/filter/sharedFilter.scss';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import '../../dashboard.scss';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CSV_URL = 'https://d3ijhv7dn0xr3b.cloudfront.net/10186.csv';
const YEARS = ['2020-21', '2021-22', '2022-23'];
const COLORS = ['#05a34a', '#fbbc06', '#5970ca', '#a1edec', '#8B0000'];

interface YearRow {
  Acac_yr: string;
  Government: number;
  fullyAided: number;
  unAided: number;
  partiallyAided: number;
  CentralGovt: number;
}

const TotalEnrolmentOverTime: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const usertypeid = s.usertypeid ?? '';
  const emisUsertype1 = s.emisUsertype1 ?? '';
  const districtId = String(s.districtId ?? '');
  const edu_district_id = String(s.edu_district_id ?? '');
  const blockId = String(s.blockId ?? '');

  const [overallData, setOverallData] = useState<any[]>([]);
  const [finalList, setFinalList] = useState<YearRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [filterState, setFilterState] = useState<FilterState | null>(null);

  // Load CSV once
  useEffect(() => {
    Papa.parse(CSV_URL, {
      download: true,
      complete: (results) => {
        let rows = parseCsvToObjects(results.data as string[][]);
        if (usertypeid === '24' && ['5', '6'].includes(emisUsertype1)) {
          rows = rows.filter((x) => String(x.manage_id) === '2');
        }
        setOverallData(rows);
        setLoading(false);
      },
      error: () => setLoading(false),
    });
  }, [usertypeid, emisUsertype1]);

  // Recalculate when overallData or filters change
  useEffect(() => {
    if (overallData.length === 0) return;

    // NOTE: For TotalEnrollmentOverTime, the school type filter controls which
    // datasets appear in the chart (handled in chartData below), NOT the raw data filtering.
    // So we skip calculateFilteredData here — it would incorrectly filter by school_type_id
    // which doesn't match this CSV's field structure.
    const geoType = getMngTypeOnUserType(usertypeid, emisUsertype1);
    let filtered = overallData;

    if (typeof geoType === 'number' || Array.isArray(geoType)) {
      const ids = ([] as number[]).concat(geoType as any).map(String);
      filtered = overallData.filter((x) => ids.includes(String(x.manage_id)));
    } else if (geoType === 'district') {
      filtered = overallData.filter(
        (x) => String(x.district_id) === districtId
      );
    } else if (geoType === 'edu_district') {
      filtered = overallData.filter(
        (x) => String(x.edu_dist_id) === edu_district_id
      );
    } else if (geoType === 'block') {
      filtered = overallData.filter((x) => String(x.block_id) === blockId);
    }

    const rows: YearRow[] = YEARS.map((yr) => {
      const yearData = filtered.filter((x) => x.Acac_yr === yr);
      return {
        Acac_yr: yr,
        Government: sumField(
          yearData.filter((x) => x.school_type === 'Government'),
          'count(scd.id)'
        ),
        fullyAided: sumField(
          yearData.filter((x) => x.school_type === 'Fully Aided'),
          'count(scd.id)'
        ),
        unAided: sumField(
          yearData.filter((x) => x.school_type === 'Un-aided'),
          'count(scd.id)'
        ),
        partiallyAided: sumField(
          yearData.filter((x) => x.school_type === 'Partially Aided'),
          'count(scd.id)'
        ),
        CentralGovt: sumField(
          yearData.filter((x) => x.school_type === 'Central Govt'),
          'count(scd.id)'
        ),
      };
    });
    queueMicrotask(() => setFinalList(rows));
  }, [
    overallData,
    usertypeid,
    emisUsertype1,
    districtId,
    edu_district_id,
    blockId,
  ]);

  const handleFilterChange = useCallback((filters: FilterState) => {
    setFilterState(filters);
  }, []);

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  // Build chart datasets based on filter state — only show checked school types
  const datasets = [];
  if (!filterState || filterState.governmentChk) {
    datasets.push({
      label: 'Government',
      data: finalList.map((r) => r.Government),
      backgroundColor: COLORS[0],
    });
  }
  if (!filterState || filterState.fullyAidedChk) {
    datasets.push({
      label: 'Fully Aided',
      data: finalList.map((r) => r.fullyAided),
      backgroundColor: COLORS[1],
    });
  }
  if (!filterState || filterState.partiallyAidedChk) {
    datasets.push({
      label: 'Partially Aided',
      data: finalList.map((r) => r.partiallyAided),
      backgroundColor: COLORS[2],
    });
  }
  if (!filterState || filterState.unAidedChk) {
    datasets.push({
      label: 'Un Aided',
      data: finalList.map((r) => r.unAided),
      backgroundColor: COLORS[3],
    });
  }
  if (!filterState || filterState.centralGovtChk) {
    datasets.push({
      label: 'Central Govt',
      data: finalList.map((r) => r.CentralGovt),
      backgroundColor: COLORS[4],
    });
  }

  const chartData = {
    labels: YEARS,
    datasets,
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: 'top' as const },
      tooltip: { mode: 'index' as const },
    },
    scales: {
      x: { stacked: false },
      y: {
        stacked: false,
        title: { display: true, text: 'Enrollment Number' },
      },
    },
  };

  const chartContent = (
    <>
      <div className="d-flex justify-content-center align-items-center">
        <p className="text-muted small">(Hover on the Graph to view Numbers)</p>
      </div>
      <Bar data={chartData} options={options} />
    </>
  );

  const gridContent = (
    <div className="comparative-grid mt-3">
      <div className="mb-2">
        <input
          type="text"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
          className="global-search-input"
        />
      </div>
      <DataTable
        value={finalList}
        sortMode="single"
        size="small"
        stripedRows
        emptyMessage="No data found"
        globalFilter={globalFilter}
        globalFilterFields={[
          'Acac_yr',
          'Government',
          'fullyAided',
          'partiallyAided',
          'unAided',
          'CentralGovt',
        ]}
      >
        <Column field="Acac_yr" header="Academic Year" sortable />
        {(!filterState || filterState.governmentChk) && (
          <Column
            field="Government"
            header="Government"
            sortable
            body={(row) => row.Government.toLocaleString('en-IN')}
          />
        )}
        {(!filterState || filterState.fullyAidedChk) && (
          <Column
            field="fullyAided"
            header="Fully Aided"
            sortable
            body={(row) => row.fullyAided.toLocaleString('en-IN')}
          />
        )}
        {(!filterState || filterState.partiallyAidedChk) && (
          <Column
            field="partiallyAided"
            header="Partially Aided"
            sortable
            body={(row) => row.partiallyAided.toLocaleString('en-IN')}
          />
        )}
        {(!filterState || filterState.unAidedChk) && (
          <Column
            field="unAided"
            header="Un Aided"
            sortable
            body={(row) => row.unAided.toLocaleString('en-IN')}
          />
        )}
        {(!filterState || filterState.centralGovtChk) && (
          <Column
            field="CentralGovt"
            header="Central Govt"
            sortable
            body={(row) => row.CentralGovt.toLocaleString('en-IN')}
          />
        )}
      </DataTable>
    </div>
  );
  return (
    <div className="chart-with-filter">
      <SharedFilter showFilters={['1']} onFilterChange={handleFilterChange} />
      <ChartPanelWrapper
        title=""
        chartContent={chartContent}
        gridContent={gridContent}
        loading={loading}
      />
    </div>
  );
};

export default TotalEnrolmentOverTime;
